import React, { useState, useRef } from "react";
import { TextField, Grid, Tabs, Tab, Box, Button, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Link from "@tiptap/extension-link";

import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  MenuButtonUnderline,
  MenuButtonOrderedList,
  MenuButtonImageUpload,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonHorizontalRule,
  MenuButtonBulletedList,
  MenuButtonEditLink,
} from "mui-tiptap";

export default function SkullIslandConfig({
  gameConfig,
  setGameConfig,
  loading = false,
  gameProvider,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const rteRef = useRef(null);
  const handleInputChange = (event, rowIndex) => {
    const { name, value } = event.target;

    if (name.startsWith("entry")) {
      const entryIndex = parseInt(name.replace("entry", "")) - 1;
      const newEntries = [...(gameConfig.entries || [])];
      newEntries[entryIndex] = parseInt(value) || 0;
      setGameConfig({
        ...gameConfig,
        entries: newEntries,
      });
    } else if (typeof rowIndex === 'number') {
      // Editing a row in config
      const newConfig = [...(gameConfig.config || [])];
      newConfig[rowIndex] = {
        ...newConfig[rowIndex],
        [name]: value,
      };
      setGameConfig({
        ...gameConfig,
        config: newConfig,
      });
    } else {
      setGameConfig({
        ...gameConfig,
        [name]: value,
      });
    }
  };

  const handleAddRow = () => {
    const newConfig = [...(gameConfig.config || [])];
    newConfig.push({ multiplier: '', winProbability: '', enemy: '', coins: '' });
    setGameConfig({
      ...gameConfig,
      config: newConfig,
    });
  };

  const handleDeleteRow = (rowIndex) => {
    const newConfig = [...(gameConfig.config || [])];
    newConfig.splice(rowIndex, 1);
    setGameConfig({
      ...gameConfig,
      config: newConfig,
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleHowToPlayChange = (html) => {
    setGameConfig({
      ...gameConfig,
      howToPlay: {
        ...gameConfig.howToPlay,
        en: html,
      },
    });
  };

  const handleAddImage = () => {
    // Placeholder for image upload logic
    const url = prompt("Enter the image URL:");
    if (url && rteRef.current) {
      rteRef.current.editor
        .chain()
        .focus()
        .setImage({ src: url })
        .run();
    }
  };

  return (
    <Box>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label="Skull Island Config Tabs">
        <Tab label="Rows" />
        <Tab label="Entries" />
        <Tab label="How To Play" />
      </Tabs>
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && (
          <>
            {/* ...existing code for Rows tab... */}
            <Grid container spacing={3}>
              {gameConfig &&
                gameConfig.config &&
                gameConfig.config.map((entry, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={3}>
                      <TextField
                        name="multiplier"
                        label="Multiplier"
                        fullWidth
                        value={entry.multiplier}
                        onChange={(e) => handleInputChange(e, index)}
                        variant="outlined"
                        disabled={loading}
                        type="number"
                        step="0.1"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        name="winProbability"
                        label="Win Probability"
                        fullWidth
                        value={entry.winProbability}
                        onChange={(e) => handleInputChange(e, index)}
                        variant="outlined"
                        disabled={loading}
                        type="number"
                        step="0.1"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        name="enemy"
                        label="Enemy"
                        fullWidth
                        value={entry.enemy}
                        onChange={(e) => handleInputChange(e, index)}
                        variant="outlined"
                        disabled={loading}
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        name="coins"
                        label="Coins"
                        fullWidth
                        value={entry.coins}
                        onChange={(e) => handleInputChange(e, index)}
                        variant="outlined"
                        disabled={loading}
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={() => handleDeleteRow(index)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}

            </Grid>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddRow}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              Add Row
            </Button>
          </>
        )}
        {activeTab === 1 && (
          <>
            {/* ...existing code for Entries tab... */}
            <Grid container spacing={3}>
              {gameConfig &&
                gameConfig.entries &&
                gameConfig.entries.map((entry, index) => (
                  <React.Fragment key={`entry-${index}`}>
                    <Grid item xs={3}>
                      <TextField
                        name={`entry${index + 1}`}
                        label={`Entry ${index + 1}`}
                        fullWidth
                        value={gameConfig.entries[index] || ""}
                        onChange={handleInputChange}
                        variant="outlined"
                        disabled={loading}
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        aria-label="delete-entry"
                        color="error"
                        onClick={() => {
                          const newEntries = [...(gameConfig.entries || [])];
                          newEntries.splice(index, 1);
                          setGameConfig({
                            ...gameConfig,
                            entries: newEntries,
                          });
                        }}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}
            </Grid>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const newEntries = [...(gameConfig.entries || [])];
                newEntries.push(0);
                setGameConfig({
                  ...gameConfig,
                  entries: newEntries,
                });
              }}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              Add Entry
            </Button>
          </>
        )}
        {activeTab === 2 && (
          <Box sx={{ p: 2 }}>
            <RichTextEditor
              ref={rteRef}
              key="en-editor" // Force re-render when switching tabs
              extensions={[
                StarterKit,
                TextStyle,
                Underline,
                BulletList,
                ListItem,
                Link.configure({
                  openOnClick: true,
                  autolink: true,
                }),
                Image.configure({
                  inline: true,
                }),
              ]}
              content={gameConfig?.howToPlay?.en || ""}
              onUpdate={({ editor }) => {
                const html = editor.getHTML();
                handleHowToPlayChange(html);
              }}
              renderControls={() => (
                <MenuControlsContainer>
                  <MenuSelectHeading />
                  <MenuDivider />
                  <MenuButtonBold />
                  <MenuButtonItalic />
                  <MenuButtonUnderline />
                  <MenuButtonOrderedList />
                  <MenuButtonBulletedList />
                  <MenuButtonUndo />
                  <MenuButtonRedo />
                  <MenuButtonHorizontalRule />
                  <MenuDivider />

                  <MenuButtonEditLink
                    onClick={() => {
                      const url = prompt("Enter the URL:");
                      if (url) {
                        rteRef.current.editor
                          .chain()
                          .focus()
                          .setLink({ href: url })
                          .run();
                      }
                    }}
                  />
                  <MenuButtonImageUpload
                    onClick={handleAddImage}
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </MenuControlsContainer>
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
